/* Geodesy on the WGS84 ellipsoid.
 *
 * GEOSPATIAL-DATA-SOURCES.md §7 / FR-GEO-07: distance and bearing must be geodesic,
 * not spherical. At operational ranges the difference is real — a spherical great
 * circle can be several hundred metres wrong over 300 nm, which matters when the
 * number is a weapon range.
 *
 * Production uses GeographicLib (via Atlas). This is a self-contained Vincenty
 * implementation so the client can compute ranges without a round trip.
 */

const A = 6378137.0 // WGS84 semi-major axis, metres
const F = 1 / 298.257223563 // flattening
const B = A * (1 - F) // semi-minor axis
const M_PER_NM = 1852

export const toRad = (d: number) => (d * Math.PI) / 180
export const toDeg = (r: number) => (r * 180) / Math.PI
export const metresToNm = (m: number) => m / M_PER_NM
export const nmToMetres = (nm: number) => nm * M_PER_NM

export interface LngLat {
  lon: number
  lat: number
}

/**
 * Vincenty direct: point at `distanceM` along `bearingDeg` from `origin`.
 * Used for envelope geometry, so it has to be accurate at 200+ nm.
 */
export function destination(origin: LngLat, bearingDeg: number, distanceM: number): LngLat {
  const α1 = toRad(bearingDeg)
  const sinα1 = Math.sin(α1)
  const cosα1 = Math.cos(α1)

  const tanU1 = (1 - F) * Math.tan(toRad(origin.lat))
  const cosU1 = 1 / Math.sqrt(1 + tanU1 * tanU1)
  const sinU1 = tanU1 * cosU1

  const σ1 = Math.atan2(tanU1, cosα1)
  const sinα = cosU1 * sinα1
  const cosSqα = 1 - sinα * sinα
  const uSq = (cosSqα * (A * A - B * B)) / (B * B)
  const AA = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)))
  const BB = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)))

  let σ = distanceM / (B * AA)
  let σPrev: number
  let cos2σM = 0
  let sinσ = 0
  let cosσ = 0
  let i = 0

  do {
    cos2σM = Math.cos(2 * σ1 + σ)
    sinσ = Math.sin(σ)
    cosσ = Math.cos(σ)
    const Δσ =
      BB *
      sinσ *
      (cos2σM +
        (BB / 4) *
          (cosσ * (-1 + 2 * cos2σM * cos2σM) -
            (BB / 6) * cos2σM * (-3 + 4 * sinσ * sinσ) * (-3 + 4 * cos2σM * cos2σM)))
    σPrev = σ
    σ = distanceM / (B * AA) + Δσ
  } while (Math.abs(σ - σPrev) > 1e-12 && ++i < 100)

  const tmp = sinU1 * sinσ - cosU1 * cosσ * cosα1
  const lat2 = Math.atan2(
    sinU1 * cosσ + cosU1 * sinσ * cosα1,
    (1 - F) * Math.sqrt(sinα * sinα + tmp * tmp),
  )
  const λ = Math.atan2(sinσ * sinα1, cosU1 * cosσ - sinU1 * sinσ * cosα1)
  const C = (F / 16) * cosSqα * (4 + F * (4 - 3 * cosSqα))
  const L =
    λ - (1 - C) * F * sinα * (σ + C * sinσ * (cos2σM + C * cosσ * (-1 + 2 * cos2σM * cos2σM)))

  return { lon: origin.lon + toDeg(L), lat: toDeg(lat2) }
}

/** Vincenty inverse — geodesic distance in metres, plus initial bearing. */
export function inverse(a: LngLat, b: LngLat): { distanceM: number; bearingDeg: number } {
  const L = toRad(b.lon - a.lon)
  const tanU1 = (1 - F) * Math.tan(toRad(a.lat))
  const cosU1 = 1 / Math.sqrt(1 + tanU1 * tanU1)
  const sinU1 = tanU1 * cosU1
  const tanU2 = (1 - F) * Math.tan(toRad(b.lat))
  const cosU2 = 1 / Math.sqrt(1 + tanU2 * tanU2)
  const sinU2 = tanU2 * cosU2

  let λ = L
  let λPrev: number
  let i = 0
  let sinλ = 0
  let cosλ = 0
  let sinσ = 0
  let cosσ = 0
  let σ = 0
  let cosSqα = 0
  let cos2σM = 0

  do {
    sinλ = Math.sin(λ)
    cosλ = Math.cos(λ)
    const sinSqσ =
      cosU2 * sinλ * (cosU2 * sinλ) +
      (cosU1 * sinU2 - sinU1 * cosU2 * cosλ) * (cosU1 * sinU2 - sinU1 * cosU2 * cosλ)
    if (sinSqσ === 0) return { distanceM: 0, bearingDeg: 0 } // coincident
    sinσ = Math.sqrt(sinSqσ)
    cosσ = sinU1 * sinU2 + cosU1 * cosU2 * cosλ
    σ = Math.atan2(sinσ, cosσ)
    const sinα = (cosU1 * cosU2 * sinλ) / sinσ
    cosSqα = 1 - sinα * sinα
    cos2σM = cosSqα !== 0 ? cosσ - (2 * sinU1 * sinU2) / cosSqα : 0
    const C = (F / 16) * cosSqα * (4 + F * (4 - 3 * cosSqα))
    λPrev = λ
    λ =
      L +
      (1 - C) * F * sinα * (σ + C * sinσ * (cos2σM + C * cosσ * (-1 + 2 * cos2σM * cos2σM)))
  } while (Math.abs(λ - λPrev) > 1e-12 && ++i < 200)

  const uSq = (cosSqα * (A * A - B * B)) / (B * B)
  const AA = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)))
  const BB = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)))
  const Δσ =
    BB *
    sinσ *
    (cos2σM +
      (BB / 4) *
        (cosσ * (-1 + 2 * cos2σM * cos2σM) -
          (BB / 6) * cos2σM * (-3 + 4 * sinσ * sinσ) * (-3 + 4 * cos2σM * cos2σM)))

  const bearing = toDeg(Math.atan2(cosU2 * sinλ, cosU1 * sinU2 - sinU1 * cosU2 * cosλ))
  return { distanceM: B * AA * (σ - Δσ), bearingDeg: (bearing + 360) % 360 }
}

/** Geodesic circle as a closed ring. */
export function circleRing(centre: LngLat, radiusNm: number, steps = 96): [number, number][] {
  const r = nmToMetres(radiusNm)
  const ring: [number, number][] = []
  for (let i = 0; i <= steps; i++) {
    const p = destination(centre, (i * 360) / steps, r)
    ring.push([p.lon, p.lat])
  }
  return ring
}

/**
 * Geodesic arc sector as a closed ring: centre → arc → centre.
 * Sensor blind arcs are exploitable geometry, so an arc-limited sensor must not be
 * drawn as a full circle.
 */
export function sectorRing(
  centre: LngLat,
  radiusNm: number,
  centreBearingDeg: number,
  halfWidthDeg: number,
  steps = 64,
): [number, number][] {
  if (halfWidthDeg >= 180) return circleRing(centre, radiusNm, steps)
  const r = nmToMetres(radiusNm)
  const ring: [number, number][] = [[centre.lon, centre.lat]]
  const from = centreBearingDeg - halfWidthDeg
  const span = halfWidthDeg * 2
  for (let i = 0; i <= steps; i++) {
    const p = destination(centre, from + (span * i) / steps, r)
    ring.push([p.lon, p.lat])
  }
  ring.push([centre.lon, centre.lat])
  return ring
}

/** Format a geodesic measurement for the operator readout. */
export function formatMeasure(a: LngLat, b: LngLat): { nm: string; bearing: string; reciprocal: string } {
  const { distanceM, bearingDeg } = inverse(a, b)
  return {
    nm: metresToNm(distanceM).toFixed(1),
    bearing: String(Math.round(bearingDeg)).padStart(3, '0'),
    reciprocal: String(Math.round((bearingDeg + 180) % 360)).padStart(3, '0'),
  }
}
